import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import minhasInscricoesService from "../../../services/minhasInscricoesService";
import type { MinhaInscricao } from "../../../services/minhasInscricoesService";

const STATUS_COLORS = {
  pendente: "bg-yellow-100 text-yellow-800",
  em_analise: "bg-blue-100 text-blue-800",
  aprovado: "bg-green-100 text-green-800",
  reprovado: "bg-red-100 text-red-800",
};

const STATUS_LABELS = {
  pendente: "Pendente",
  em_analise: "Em Análise",
  aprovado: "Aprovado",
  reprovado: "Reprovado",
};

export default function MinhasInscricoes() {
  const [inscricoes, setInscricoes] = useState<MinhaInscricao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregarInscricoes = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await minhasInscricoesService.listar();
      setInscricoes(response.inscricoes);
    } catch (err) {
      setError("Erro ao carregar inscrições");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarInscricoes();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando inscrições...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Minhas Inscrições
          </h1>
          <p className="mt-2 text-gray-600">
            Gerencie suas inscrições em editais
          </p>
        </div>
        <Link
          to="/editais"
          className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition"
        >
          ➕ Nova Inscrição
        </Link>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Lista */}
      {inscricoes.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 text-lg mb-4">
            Você ainda não tem nenhuma inscrição
          </p>
          <Link
            to="/editais"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition"
          >
            Ver Editais Disponíveis
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {inscricoes.map((inscricao) => (
            <div
              key={inscricao.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">
                    {inscricao.edital_nome}
                  </h2>
                  <p className="mt-1 text-sm text-gray-600">
                    ID da inscrição: #{inscricao.id}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    STATUS_COLORS[inscricao.status]
                  }`}
                >
                  {STATUS_LABELS[inscricao.status]}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                <div>
                  <span className="text-gray-500">Criada em:</span>{" "}
                  <span className="font-medium text-gray-900">
                    {new Date(inscricao.created_at).toLocaleDateString("pt-BR")}
                  </span>
                </div>
              </div>

              <div className="border-t pt-4 flex items-center gap-3">
                <Link
                  to={`/inscricoes/${inscricao.id}`}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition text-sm"
                >
                  Ver Detalhes
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
